import React, { useEffect, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { get } from 'lodash';
import SignatureIcon from '../../assets/icons/signature';
import FormFooter from '../../containers/form-footer';
import GoBack from '../../containers/go-back';
import GridBorder from '../../modules/grid-border/grid-border';
import IconBtn from '../../modules/icon-btn/icon-btn';
import LoadDocBtn from '../../modules/load-doc-btn';
import TableHeaderWrapper from '../../modules/table-header-wrapper/table-header-wrapper';
import TableWrapper from '../../modules/table-wrapper/table-wrapper';
import UiSelect from '../../modules/ui/ui-select/ui-select';
import { CreditDetailsResponce } from '../../services/api/dto/Credits';
import { useAppDispatch } from '../../store';
import {
  getCreditAgreement,
  signCredit,
} from '../../store/actions/operations-sign';
import { setOperationResult } from '../../store/actions/sign-result';
import { RootState } from '../../types';
import currencyFormat from '../../utils/currencyFormat';
import { downloadFile, getPdfFromBase64 } from '../../utils/files/downloadFile';
import AssetsDetailsList from './assets-details-list/assets-details-list';
import AssetsDetailsTop from './assets-details-top/assets-details-top';
import './assets-credit-details-repayment.scss';
import { useSign } from 'hooks';

interface AssetsCreditDetailsRepaymentProps {
  asset: CreditDetailsResponce;
  onClose: () => void;
}

enum AssetsCreditDetailsStrings {
  HEADER_TITLE = 'Погашение займа',
  OPERATION_TITLE = 'Операция:',
  DETAILED_INFO_TITLE = 'Поставщики:',
  DOCS_TITLE = 'Подписываемые документы:',
}

const AssetsCreditDetailsRepayment: React.FC<AssetsCreditDetailsRepaymentProps> = ({
  onClose,
  asset,
}) => {
  const history = useHistory();
  const dispatch = useAppDispatch();
  const loadingClose = useSelector(
    (state: RootState) => state.creditDetails.loading.close
  );
  const organizationId = useSelector(
    (state: RootState) => state.auth.profile.organizationId
  );
  const companyInfo = useSelector(
    (state: RootState) => state.companiesInfo.companies
  );
  const doc = useSelector(
    (state: RootState) => state.operationsSign.data.credit.decisionDoc
  );
  const hash = useSelector(
    (state: RootState) => state.operationsSign.data.credit.decisionHash
  );

  const {
    certificatesList,
    selectedCertificate,
    signEntity,
    onUserSelectCertificate,
  } = useSign();

  useEffect(() => {
    dispatch(getCreditAgreement({ creditId: asset.id, type: 'REPAYMENT' }));
  }, [asset.id]);

  const topDesc = useMemo(
    () => `Погашение займа ${currencyFormat(asset.amount)}`,
    [asset.amount]
  );

  const detailsList = useMemo(
    () => [
      [
        {
          title: 'Заемщик:',
          value: organizationId && companyInfo[organizationId].shortName,
        },
        {
          title: 'ИНН:',
          value: organizationId && companyInfo[organizationId].tin,
        },
      ],
      [
        {
          title: 'Кредитор:',
          value: get(asset, 'creditor', '-'),
        },
        {
          title: 'ИНН:',
          value: get(asset, 'creditorTin', '-'),
        },
      ],
    ],
    []
  );

  const decisionDocs = useMemo(() => {
    const docs = [];

    if (doc) {
      docs.push(
        <LoadDocBtn
          title="Договор займа"
          onSpecDownload={() => {
            const resultBlob = getPdfFromBase64(doc);
            downloadFile(resultBlob, 'Договор займа.pdf', 'application/pdf');
          }}
        />
      );
    }
    return docs;
  }, [doc]);

  const onCloseCredit = async () => {
    if (hash) {
      const { pdfSigned } = await signEntity(hash);

      if (doc && pdfSigned) {
        dispatch(
          signCredit({
            type: 'REPAYMENT',
            operationId: asset.id,
            signature: pdfSigned,
            hash,
          })
        ).then((action) => {
          const status = action.meta.requestStatus === 'fulfilled';
          dispatch(setOperationResult({ name: 'credit', status }));
          history.push(`/credit/repayment-result`);
        });
      }
    }
  };

  return (
    <TableWrapper>
      <div className="assets-details">
        <div className="assets-details__header">
          <TableHeaderWrapper
            // isHint
            // hintPage="CREDIT_ASSET_DETAILS"
            leftContent={AssetsCreditDetailsStrings.HEADER_TITLE}
            onCloseClick={onClose}
          />
        </div>
        <div className="assets-details__body">
          <GridBorder
            infoTitle={
              <AssetsDetailsTop
                title={AssetsCreditDetailsStrings.OPERATION_TITLE}
                desc={topDesc}
              />
            }
            leftContent={detailsList.map((dL, index) => (
              <div className="assets-details__col assets-details__col-1">
                <AssetsDetailsList
                  list={dL}
                  title={
                    !index
                      ? AssetsCreditDetailsStrings.DETAILED_INFO_TITLE
                      : '   '
                  }
                />
              </div>
            ))}
          />
          <div className="grid-border__content">
            {decisionDocs?.length && (
              <div className="assets-details__col assets-details__col-2">
                <h2 className="text-2 assets-details__title">
                  {AssetsCreditDetailsStrings.DOCS_TITLE}
                </h2>
                {decisionDocs.map((decisionDoc, index) => (
                  <div
                    className="assets-details__item assets-details__item--mb-lg"
                    key={`assets-details__item-isDocsList-${index}`}
                  >
                    <div className="form-item">{decisionDoc}</div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
      <FormFooter>
        <div className="form-assets__container form-asset-credit__container">
          <div className="footer__go-back">
            <GoBack
              onClick={() =>
                history.push(`/credit/details/${asset.id}`, { id: asset.id })
              }
            />
          </div>
          <div className="form-assets__flex">
            <div className="form-assets__item form-assets__item--select">
              <UiSelect
                label="Выберите подпись"
                value={selectedCertificate}
                options={certificatesList}
                lg
                onChange={onUserSelectCertificate}
              />
            </div>
          </div>
          <div className="form-assets__item">
            <div className="form-item">
              <IconBtn
                disabled={loadingClose || !selectedCertificate}
                onClick={onCloseCredit}
                icon={<SignatureIcon />}
                title="Подписать и погасить"
              />
            </div>
          </div>
        </div>
      </FormFooter>
    </TableWrapper>
  );
};

export default AssetsCreditDetailsRepayment;
